import { useMemo, useState } from 'react'
import { endOfYear, format, parse, startOfYear } from 'date-fns'
import { useSnackbar } from 'notistack'

import { DDELicensePeriodResponse } from '@img-arena/consoles-api-dde'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { updateDDELicense } from '@/components/forms/LicenseForm/utils'
import { LICENSING_PERIOD_YEARS } from '@/components/inputs/LicensingPeriods/dde/LicensingPeriod.dde'
import { DATE_FORMAT } from '@/constants/dateFormat'
import { USE_COMPETITIONS, UseCompetitionsType } from '@/constants/licenses'
import type { LicensePropertyOperatorData } from '@/stores/licenseProperty'
import type { FormattedOperatorWithLicense } from '@/types/operator'
import { notifyError } from '@/utils/notifyError'

import { CheckboxMenuItem } from '../CheckboxMenuItem/CheckboxMenuItem'
import { useCheckboxMenuContext } from '../context/CheckboxMenuContext'

type CheckboxRenewLicenseForOperatorsItemProps = {
  licenseProperty: string
  onSuccess?: () => void
}

const getOperatorLicensePeriodYears = (licensePeriods: DDELicensePeriodResponse[]): number[] => {
  const today = new Date()
  const years = licensePeriods.flatMap((period) => {
    const startYear = parse(period.startDate, DATE_FORMAT.DATE, today).getFullYear()
    const endYear = parse(period.endDate, DATE_FORMAT.DATE, today).getFullYear()
    return [startYear, endYear]
  })
  return Array.from(new Set(years))
}

const getOperatorLicensePropertyYears = (
  operator: FormattedOperatorWithLicense,
  property: string
): number[] => {
  if (!operator?.license?.length) return []
  const licenseProperty = operator.license.find((lic) => lic.property === property)
  if (!licenseProperty) return []
  return getOperatorLicensePeriodYears(licenseProperty.licensingPeriods)
}

export function CheckboxRenewLicenseForOperatorsItem({
  licenseProperty,
  onSuccess
}: CheckboxRenewLicenseForOperatorsItemProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { rows, closeMenu, deselectAll } = useCheckboxMenuContext<LicensePropertyOperatorData>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedYears, setSelectedYears] = useState<string[]>([])
  const [isProcessing, setIsProcessing] = useState(false)

  const currentYear = new Date().getFullYear()

  const existingYears = useMemo(() => {
    const allYears = rows.flatMap((op) => getOperatorLicensePropertyYears(op, licenseProperty))
    return Array.from(new Set(allYears)).sort((a, b) => a - b)
  }, [rows, licenseProperty])

  const availableYears = useMemo(() => {
    const startIndex = LICENSING_PERIOD_YEARS.indexOf(currentYear.toString())
    if (startIndex === -1) return LICENSING_PERIOD_YEARS
    return LICENSING_PERIOD_YEARS.slice(startIndex)
  }, [currentYear])

  const handleOpenDialog = () => {
    setSelectedYears([])
    setDialogOpen(true)
  }

  const handleYearToggle = (year: string) => {
    const yearNum = parseInt(year)
    if (existingYears.includes(yearNum)) return

    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    )
  }

  const handleRenewLicense = async () => {
    if (selectedYears.length === 0) return

    setIsProcessing(true)
    const results = await Promise.allSettled(
      rows.map(async (operator) => {
        const licenseProperty_obj = operator.license.find((lic) => lic.property === licenseProperty)
        if (!licenseProperty_obj || !licenseProperty_obj.licensingPeriods.length) {
          throw new Error(`No license found for ${operator.name}`)
        }

        const lastPeriod =
          licenseProperty_obj.licensingPeriods[licenseProperty_obj.licensingPeriods.length - 1]

        const newPeriods: DDELicensePeriodResponse[] = selectedYears.map((yearStr) => {
          const year = parseInt(yearStr)
          return {
            ...lastPeriod,
            id: 0,
            startDate: format(startOfYear(new Date(year, 0, 1)), 'yyyy-MM-dd'),
            endDate: format(endOfYear(new Date(year, 11, 31)), 'yyyy-MM-dd')
          }
        })

        await updateDDELicense({
          operatorId: String(operator.id),
          licensePeriods: newPeriods,
          licenseProperty: {
            value: licenseProperty,
            label: licenseProperty
          },
          selectedCompetitionsOrSeasons: [],
          competitionIds: lastPeriod?.subscription?.competitionIds || [],
          useAllCompetitions:
            (lastPeriod?.subscription?.type as UseCompetitionsType) || USE_COMPETITIONS.PARTIAL
        })

        return operator.name
      })
    )

    const successful = results.filter((r) => r.status === 'fulfilled')
    const failed = results.filter((r) => r.status === 'rejected')

    if (successful.length > 0) {
      enqueueSnackbar(
        `Successfully renewed license for ${successful.length} operator(s) for ${selectedYears.length} year(s)`,
        { variant: 'success' }
      )
    }

    if (failed.length > 0) {
      failed.forEach((result) => {
        if (result.status === 'rejected') {
          notifyError(`Failed to renew license: ${result.reason.message}`)
        }
      })
    }

    setIsProcessing(false)
    setDialogOpen(false)
    deselectAll?.()
    closeMenu?.()
    onSuccess?.()
  }

  const disabled = rows.length === 0

  return (
    <>
      <CheckboxMenuItem
        onClick={handleOpenDialog}
        disabled={disabled}
        icon={<AutorenewIcon />}
        text={`Renew License`}
      />
      <ConsoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        description={`Select years to add license periods for ${rows.length} selected operator${rows.length > 1 ? 's' : ''}.`}
        title={`Renew License: ${licenseProperty}`}
        SaveButtonProps={{
          onClick: handleRenewLicense,
          text: 'Add License Periods',
          loading: isProcessing,
          disabled: selectedYears.length === 0 || isProcessing
        }}
        CloseButtonProps={{
          text: 'Cancel',
          disabled: isProcessing
        }}
      >
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: 1.5,
              maxHeight: '400px',
              overflowY: 'auto'
            }}
          >
            {availableYears.map((year) => {
              const yearNum = parseInt(year)
              const isExisting = existingYears.includes(yearNum)
              const isSelected = selectedYears.includes(year)

              return (
                <Chip
                  key={year}
                  label={year}
                  onClick={() => handleYearToggle(year)}
                  color="primary"
                  variant={isExisting || isSelected ? 'filled' : 'outlined'}
                  disabled={isExisting}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 600 : 400,
                    cursor: isExisting ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      backgroundColor: isExisting || isSelected ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
              )
            })}
          </Box>

          {existingYears.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Disabled years already have license periods for the selected operators. It is not
              possible to edit previous years.
            </Typography>
          )}
        </Stack>
      </ConsoleDialog>
    </>
  )
}
